// Utility functions to trigger server-side counter recalculation after CRUD operations.
// Call these after any create/update/delete of child records (units, devices, people, guest_pins, ACS).

const AWS_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

function getToken(): string {
  return localStorage.getItem('intrlock_token') || '';
}

async function postRefresh(url: string): Promise<void> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`,
      },
    });
    if (!res.ok) {
      console.warn(`Counter refresh returned ${res.status} for ${url}`);
    }
  } catch (e) {
    console.warn('Counter refresh failed:', e);
  }
}

/**
 * Recalculate all counters for a property.
 * Call after any create/update/delete of units, devices, people, guest_pins, or ACS systems.
 */
export async function refreshPropertyCounters(propertyId: string): Promise<void> {
  await postRefresh(`${AWS_BASE}/properties/${propertyId}/refresh-counters`);
}

/**
 * Recalculate all counters for a customer (sums across all their properties).
 * Call after adding/removing a property, or after a property's counters change.
 */
export async function refreshCustomerCounters(customerId: string): Promise<void> {
  await postRefresh(`${AWS_BASE}/customers/${customerId}/refresh-counters`);
}

/**
 * Recalculate all counters for a dealer (sums across all their customers).
 * Call after adding/removing a customer, or after a customer's counters change.
 */
export async function refreshDealerCounters(dealerId: string): Promise<void> {
  await postRefresh(`${AWS_BASE}/dealers/${dealerId}/refresh-counters`);
}

/**
 * Convenience: refresh the property and its parent customer in one call.
 * Use this after mutations to units, devices, people, guest_pins, or ACS systems.
 */
export async function refreshPropertyAndCustomer(propertyId: string, customerId: string): Promise<void> {
  await refreshPropertyCounters(propertyId);
  await refreshCustomerCounters(customerId);
}

/**
 * Convenience: refresh the full chain — property → customer → dealer.
 * Use this for mutations that affect revenue or device counts that roll up to the dealer.
 */
export async function refreshFullChain(
  propertyId: string,
  customerId: string,
  dealerId: string
): Promise<void> {
  await refreshPropertyCounters(propertyId);
  await refreshCustomerCounters(customerId);
  await refreshDealerCounters(dealerId);
}

/**
 * Refresh ALL counters across all dealers, customers, and properties.
 * Use after any create, update, delete, or assign operation.
 */
export async function refreshAllCounters(): Promise<void> {
  await postRefresh(`${AWS_BASE}/counters/refresh`);
}
